import { useCallback, useEffect, useMemo, useRef } from 'react';

import { startMeetingStream, startMicStream } from '../audioClient';
import { AUDIO_SOURCES } from '../constants/audioSources';

/**
 * Wraps the existing PCM-capture pipeline in audioClient.js so pages never
 * call startMicStream/startMeetingStream directly. Keeps the underlying
 * buffer conversion, ScriptProcessorNode wiring, and echo-prevention logic
 * fully reused/untouched.
 */
export function useAudio() {
  const cleanupRef = useRef(null);
  // True while a start() call is between "picker/WS handshake in flight"
  // and "cleanupRef registered". A second start() invoked in that window
  // (e.g. a double-click on "Select Meeting Tab" before React re-renders
  // the button away) used to see nothing in cleanupRef to tear down and
  // would open a second, independent MediaStream/AudioContext/WebSocket
  // for the same meeting — the loser silently overwrote cleanupRef,
  // leaking the other connection, which then failed on its own later and
  // surfaced a confusing "disconnected"/"unable to connect" error even
  // though the visible session was fine.
  const startingRef = useRef(false);

  const stop = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  const start = useCallback(
    async (
      source,
      wsUrl,
      onMessage,
      onStopped,
      deviceId,
      onDisconnected,
      // Optional. { onMicrophoneUnavailable } - for meeting-tab capture,
      // which also records the local microphone (see audioClient.js).
      captureOptions
    ) => {
      if (startingRef.current) {
        throw new Error('Audio capture is already starting.');
      }
      startingRef.current = true;

      try {
        // Tear down any previous capture pipeline (mic MediaStream,
        // AudioContext, ScriptProcessorNode) before opening a new one.
        // Without this, an unexpected disconnect followed by a reconnect
        // attempt (see LiveMeetingPage's handleDisconnected) would just
        // overwrite cleanupRef.current with the new handle, leaking the
        // old microphone stream and AudioContext — still capturing audio
        // and holding mic access — on every reconnect.
        stop();

        const handleStopped = () => {
          cleanupRef.current = null;
          if (onStopped) onStopped();
        };

        let cleanup;

        // Explicit switch, not a truthy/ternary fallback: an unrecognized
        // source used to silently fall through to startMeetingStream(),
        // which would open the browser's display-media picker for a value
        // nobody asked for. Every source must be named here on purpose.
        switch (source) {
          case AUDIO_SOURCES.MICROPHONE:
            cleanup = await startMicStream(
              wsUrl,
              onMessage,
              handleStopped,
              deviceId,
              onDisconnected,
              // Only passed when asked for, so every other caller makes
              // exactly the call it always did. See startMicStream.
              ...(captureOptions?.hearOthers
                ? [{ hearOthers: true }]
                : [])
            );
            break;

          case AUDIO_SOURCES.MEETING_TAB:
            // The shared tab only carries what the tab PLAYS (remote
            // participants); the user's own voice needs the microphone.
            cleanup = await startMeetingStream(
              wsUrl,
              onMessage,
              handleStopped,
              onDisconnected,
              {
                includeMicrophone: true,
                microphoneDeviceId: deviceId,
                onMicrophoneUnavailable:
                  captureOptions?.onMicrophoneUnavailable,
              }
            );
            break;

          case AUDIO_SOURCES.MIXED:
            // Deferred (see StartMeetingPage.jsx) — not selectable from
            // the UI yet, but fails loudly rather than silently
            // misbehaving if something ever reaches this with it.
            throw new Error(
              'Microphone + Meeting (mixed) audio is not yet supported.'
            );

          default:
            throw new Error(`Unsupported audio source: ${source}`);
        }

        cleanupRef.current = cleanup;
      } finally {
        startingRef.current = false;
      }
    },
    [stop]
  );

  // Sends a control message (currently: retry a failed answer or
  // translation) over the live audio socket. Returns false when there
  // is no open socket to send on, so callers can tell the user their
  // click did nothing rather than silently dropping it.
  const sendControl = useCallback((payload) => {
    const handle = cleanupRef.current;

    if (!handle || typeof handle.sendControl !== 'function') {
      return false;
    }

    return handle.sendControl(payload);
  }, []);

  useEffect(() => stop, [stop]);

  // A STABLE aggregate. start/stop/sendControl are each useCallback-
  // stable for the hook's lifetime, so this object's identity never
  // changes after mount. Returning a fresh `{ start, stop, sendControl }`
  // literal each render (as this used to) made every consumer that put
  // `audio` in an effect dependency list re-run that effect on every
  // unrelated rerender — which, for LiveMeetingPage's page-leave effect,
  // meant its cleanup fired during ordinary rerenders and tore the
  // meeting down. Consumers should still prefer destructuring the
  // individual callbacks; this memo is the defence in depth.
  return useMemo(
    () => ({ start, stop, sendControl }),
    [start, stop, sendControl]
  );
}
